import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, Loader2, Lock, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import VehicleIdentifier from "@/components/VehicleIdentifier";
import DtcInput from "@/components/DtcInput";
import FileDropzone from "@/components/FileDropzone";
import SlotPicker from "@/components/SlotPicker";

const EMPTY = { plate: "", vin: "", make: "", model: "", year: "", displacement: "", power_kw: "", fuel: "", engine_code: "", engine: "" };

function Step({ n, title, children, testid }) {
  return (
    <section className="surface p-5 sm:p-7 fade-up" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <span className="mono text-xs bg-[#E50914] text-white w-7 h-7 grid place-items-center rounded-sm font-bold">{n}</span>
        <h2 className="font-display text-2xl font-bold uppercase">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Paywall() {
  return (
    <div className="surface p-10 text-center max-w-xl mx-auto" data-testid="new-ticket-paywall">
      <Lock className="mx-auto text-[#E50914] mb-4" size={36} />
      <h1 className="font-display text-3xl font-bold uppercase">Crediti necessari</h1>
      <p className="text-slate-400 mt-3">Per aprire un nuovo ticket e prenotare uno slot servono crediti attivi o un abbonamento valido.</p>
      <Link to="/crediti" className="btn-red inline-block px-6 py-3 mt-6" data-testid="paywall-buy-button">Ricarica Crediti / Acquista Supporto</Link>
    </div>
  );
}

function Attachments({ files, setFiles }) {
  return (
    <div>
      <FileDropzone testid="attachments-dropzone" accept="image/*,video/*" onFiles={(l) => setFiles([...files, ...l])}
        title="Foto / video preliminari" hint="Trascina o seleziona · max 50MB per file" />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="attachments-list">
          {files.map((f, i) => (
            <span key={i} className="tag flex items-center gap-2 max-w-[240px]">
              <span className="truncate">{f.name}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NewTicket() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(EMPTY);
  const [description, setDescription] = useState("");
  const [dtc, setDtc] = useState([]);
  const [files, setFiles] = useState([]);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  if (!(user.credits > 0 || user.subscription_active)) return <Paywall />;
  const missing = [
    !vehicle.make.trim() && "Marca",
    !(vehicle.plate.trim() || vehicle.vin.trim()) && "Targa o VIN",
    description.trim().length < 5 && "Descrizione difetto",
    !slot && "Slot data/ora",
    !user.phone && "Telefono nel profilo",
  ].filter(Boolean);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", { vehicle, description, dtc_codes: dtc, slot_date: slot.date, slot_time: slot.time });
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/tickets/${data.id}/files`, fd).catch((e) => toast.error(`Ticket creato, ma upload allegati fallito: ${errMsg(e)}`));
      }
      toast.success(`Ticket #${data.number} prenotato`);
      refreshUser();
      navigate(`/ticket/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      if (e.response?.status === 409) {
        setSlot(null);
        setReloadKey((k) => k + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8">
      <div className="space-y-6 min-w-0">
        <div>
          <div className="eyebrow mb-2">Ticket di supporto</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase" data-testid="new-ticket-title">Nuova Richiesta di Supporto</h1>
        </div>
        <Step n="01" title="Identificazione veicolo" testid="step-vehicle">
          <VehicleIdentifier vehicle={vehicle} setVehicle={setVehicle} />
        </Step>
        <Step n="02" title="Difetto riscontrato" testid="step-defect">
          <label className="label">Descrizione del difetto</label>
          <textarea className="field min-h-[130px]" value={description} data-testid="defect-description-input"
            placeholder="Sintomi, quando si presenta, interventi già eseguiti…" onChange={(e) => setDescription(e.target.value)} />
          <label className="label mt-5">Codici errore DTC</label>
          <DtcInput codes={dtc} setCodes={setDtc} />
          <label className="label mt-5">Allegati</label>
          <Attachments files={files} setFiles={setFiles} />
        </Step>
        <Step n="03" title="Slot appuntamento" testid="step-slot">
          <SlotPicker value={slot} onChange={setSlot} reloadKey={reloadKey} />
        </Step>
      </div>
      <aside className="lg:sticky lg:top-24 self-start surface p-6 space-y-4" data-testid="ticket-summary">
        <div className="eyebrow">Riepilogo</div>
        <div className="flex items-center gap-2 text-sm">
          <CalendarCheck size={16} className="text-[#E50914]" />
          {slot ? <span className="text-white capitalize" data-testid="summary-slot">{fmtDate(slot.date)} · <span className="mono">{slot.time}</span></span> : <span className="text-slate-500">Nessuno slot selezionato</span>}
        </div>
        <div className="text-sm text-slate-400">
          Costo: {user.subscription_active ? <span className="text-green-400">incluso nell'abbonamento</span> : <span className="text-white mono">1 credito</span>}
          <span className="block text-xs mt-1">Saldo attuale: <span className="mono">{user.credits}</span></span>
        </div>
        {missing.length > 0 && (
          <div className="text-xs text-amber-300" data-testid="missing-fields">
            Mancano: {missing.join(", ")}
            {!user.phone && <Link to="/profilo" className="block underline mt-1">Aggiungi telefono</Link>}
          </div>
        )}
        <button onClick={submit} disabled={busy || missing.length > 0} className="btn-red w-full py-3 flex items-center justify-center gap-2"
          data-testid="submit-ticket-button">
          {busy && <Loader2 size={16} className="animate-spin" />} Invia richiesta e prenota
        </button>
      </aside>
    </div>
  );
}
